import { db } from '@/lib/db';
import { enqueue } from '@/lib/outbox';
import { reloadFromCache } from '@/state/data';
import { computeNextDue } from '@/lib/due';
import type { Animal, FeedLog, Schedule, Timer } from '@/types';

/** Optimistic local-first mutations: write Dexie (including a local
 *  next_due_at recompute that mirrors the server trigger), refresh signals,
 *  queue the remote write. */

function uuid(): string {
  return crypto.randomUUID();
}
const nowISO = () => new Date().toISOString();

async function recomputeLocal(scheduleId: string) {
  const s = await db.schedules.get(scheduleId);
  if (!s) return;
  const logs = await db.feed_logs.where('schedule_id').equals(scheduleId).toArray();
  const lastFed = logs.length
    ? logs.reduce((m, l) => (l.fed_at > m ? l.fed_at : m), logs[0].fed_at)
    : null;
  const updated: Schedule = { ...s, last_fed_at: lastFed };
  updated.next_due_at = computeNextDue(updated)?.toISOString() ?? null;
  await db.schedules.put(updated);
}

export interface LogFeedingArgs {
  animalId: string;
  scheduleId?: string | null;
  fedAt?: Date;
  amount?: number | null;
  unit?: string | null;
  food?: string | null;
  notes?: string | null;
  weightG?: number | null;
  fedBy?: string | null;
  skipped?: boolean;
  source?: FeedLog['source'];
}

export async function logFeeding(args: LogFeedingArgs): Promise<string> {
  const s = args.scheduleId ? await db.schedules.get(args.scheduleId) : null;
  const log: FeedLog = {
    id: uuid(),
    animal_id: args.animalId,
    schedule_id: args.scheduleId ?? null,
    fed_at: (args.fedAt ?? new Date()).toISOString(),
    amount: args.amount ?? s?.amount ?? null,
    unit: args.unit ?? s?.unit ?? null,
    food: args.food ?? s?.food ?? null,
    notes: args.notes ?? null,
    weight_g: args.weightG ?? null,
    fed_by: args.fedBy ?? null,
    skipped: args.skipped ?? false,
    source: args.source ?? 'manual',
    created_at: nowISO(),
  };
  await db.feed_logs.put(log);
  if (s) {
    await db.schedules.put({ ...s, snoozed_until: null });
    await recomputeLocal(s.id);
  }
  await reloadFromCache();
  await enqueue({ table: 'feed_logs', op: 'insert', values: log as never });
  return log.id;
}

export async function undoFeeding(logId: string) {
  const log = await db.feed_logs.get(logId);
  if (!log) return;
  await db.feed_logs.delete(logId);
  if (log.schedule_id) await recomputeLocal(log.schedule_id);
  await reloadFromCache();
  await enqueue({ table: 'feed_logs', op: 'delete', match: { id: logId } });
}

export async function snoozeSchedule(scheduleId: string, minutes: number) {
  const s = await db.schedules.get(scheduleId);
  if (!s) return;
  const until = new Date(Date.now() + minutes * 60_000).toISOString();
  const updated = { ...s, snoozed_until: until };
  updated.next_due_at = computeNextDue(updated)?.toISOString() ?? null;
  await db.schedules.put(updated);
  await reloadFromCache();
  await enqueue({ table: 'schedules', op: 'update', values: { snoozed_until: until }, match: { id: scheduleId } });
}

/** "Reset interval" — restart the clock from now without logging a feed. */
export async function resetScheduleInterval(scheduleId: string) {
  const s = await db.schedules.get(scheduleId);
  if (!s) return;
  const anchor = nowISO();
  const updated = { ...s, anchor_at: anchor, last_fed_at: null, snoozed_until: null };
  updated.next_due_at = computeNextDue(updated)?.toISOString() ?? null;
  await db.schedules.put(updated);
  await reloadFromCache();
  await enqueue({
    table: 'schedules',
    op: 'update',
    values: { anchor_at: anchor, last_fed_at: null, snoozed_until: null },
    match: { id: scheduleId },
  });
}

export async function createTimer(label: string, dueAt: Date, animalId?: string | null): Promise<string> {
  const t: Timer = {
    id: uuid(),
    label,
    animal_id: animalId ?? null,
    due_at: dueAt.toISOString(),
    notified_at: null,
    completed_at: null,
    canceled_at: null,
    created_at: nowISO(),
  };
  await db.timers.put(t);
  await reloadFromCache();
  await enqueue({ table: 'timers', op: 'insert', values: t as never });
  return t.id;
}

export async function closeTimer(timerId: string, how: 'completed' | 'canceled') {
  const t = await db.timers.get(timerId);
  if (!t) return;
  const patch = how === 'completed' ? { completed_at: nowISO() } : { canceled_at: nowISO() };
  await db.timers.put({ ...t, ...patch });
  await reloadFromCache();
  await enqueue({ table: 'timers', op: 'update', values: patch, match: { id: timerId } });
}

export async function upsertAnimal(animal: Partial<Animal> & { name: string }, id?: string): Promise<string> {
  const existing = id ? await db.animals.get(id) : null;
  const row: Animal = {
    id: id ?? uuid(),
    species: null,
    kind: 'individual',
    count: 1,
    category: 'other',
    status: 'active',
    condition: 'stable',
    life_stage: null,
    intake_date: null,
    released_date: null,
    diet: null,
    diet_notes: null,
    notes: null,
    cover_photo_id: null,
    created_at: existing?.created_at ?? nowISO(),
    updated_at: nowISO(),
    ...existing,
    ...animal,
  } as Animal;
  await db.animals.put(row);
  await reloadFromCache();
  const { created_at, updated_at, ...values } = row;
  void created_at; void updated_at;
  await enqueue({ table: 'animals', op: 'upsert', values: values as never });
  return row.id;
}

export type NewSchedule = Omit<Schedule, 'id' | 'created_at' | 'updated_at' | 'next_due_at' | 'last_fed_at' | 'snoozed_until'>;

export async function createSchedule(input: NewSchedule): Promise<string> {
  const row: Schedule = {
    ...input,
    id: uuid(),
    snoozed_until: null,
    last_fed_at: null,
    next_due_at: null,
    created_at: nowISO(),
    updated_at: nowISO(),
  };
  row.next_due_at = computeNextDue(row)?.toISOString() ?? null;
  await db.schedules.put(row);
  await reloadFromCache();
  const { created_at, updated_at, next_due_at, last_fed_at, ...values } = row;
  void created_at; void updated_at; void next_due_at; void last_fed_at;
  await enqueue({ table: 'schedules', op: 'insert', values: values as never });
  return row.id;
}

export async function updateSchedule(scheduleId: string, patch: Partial<Schedule>) {
  const s = await db.schedules.get(scheduleId);
  if (!s) return;
  const updated = { ...s, ...patch };
  updated.next_due_at = computeNextDue(updated)?.toISOString() ?? null;
  await db.schedules.put(updated);
  await reloadFromCache();
  await enqueue({ table: 'schedules', op: 'update', values: patch as never, match: { id: scheduleId } });
}
